import type { ApiClient, IframePoolShippingDetail } from '../../../modules/api';
import { SheetFrame } from '../../../modules/design-system';
import { SleekButton } from '../../../modules/theme';
import { useCancelDelivery } from '../../../pages/Delivery';

type Props = {
  client: ApiClient;
  shippingId: number;
  onClose: () => void;
  onCanceled: (shipping: IframePoolShippingDetail) => void;
  onNotCancelable: () => void;
};

const TITLE_ID = 'cancel-shipment-sheet-title';

/**
 * Sleek's "Cancel this delivery?" — the shared cancel request in the dark
 * bottom sheet the other sleek confirmations use, instead of the light modal.
 * Swiping it away keeps the delivery.
 */
export function CancelShipmentSheet({ client, shippingId, onClose, onCanceled, onNotCancelable }: Props) {
  const { submitting, error, confirm } = useCancelDelivery(client, shippingId, { onCanceled, onNotCancelable });
  return (
    <SheetFrame
      onClose={() => {
        if (!submitting) onClose();
      }}
      ariaLabelledBy={TITLE_ID}
      inspectName="ShipCard"
      panelClassName="bg-[var(--co-ShipCard-sheetFill,#0E0F11)] [border-top-left-radius:24px] [border-top-right-radius:24px]"
      handleClassName="bg-[var(--co-ShipCard-sheetHandle,#2F3037)]"
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <h2 id={TITLE_ID} className="m-0 text-[20px] font-semibold leading-[28px] tracking-[-0.45px] text-[var(--co-ShipCard-titleColor,#FFFFFF)]">
          Cancel this delivery?
        </h2>
        <p className="m-0 max-w-[320px] text-[14px] leading-[20px] tracking-[-0.15px] text-[var(--co-ShipCard-mutedText,#6D707C)]">
          Your card will be sold at its buyback price, and the shipping and handling charges will be refunded to your balance.
        </p>
        {error && (
          <p role="alert" className="m-0 text-[12px] leading-[16px] text-[var(--co-ShipCard-errorText,#F87171)]">
            {error}
          </p>
        )}
      </div>
      <div className="mt-6 flex flex-col gap-3">
        <SleekButton name="ShippingPrimaryButton" onClick={() => void confirm()} disabled={submitting} className="w-full">
          {submitting ? 'Canceling…' : 'Confirm cancellation'}
        </SleekButton>
        <SleekButton name="ShippingSecondaryButton" onClick={onClose} disabled={submitting} className="w-full">
          Keep delivery
        </SleekButton>
      </div>
    </SheetFrame>
  );
}
